import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      const userData = response.data;
      
      // Parse profile_keys into roles array if not already an array
      if (!userData.roles && userData.profile_keys) {
        if (typeof userData.profile_keys === 'string') {
          userData.roles = userData.profile_keys.split(',').map(r => r.trim());
        } else if (Array.isArray(userData.profile_keys)) {
          userData.roles = userData.profile_keys;
        }
      }
      
      console.log('Fetched user data:', userData);
      console.log('Parsed roles:', userData.roles);
      
      setUser(userData);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });

      const { access_token, user: userData } = response.data;
      
      console.log('Login response userData:', userData);
      console.log('Avatar URL in response:', userData.avatar_url);
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const hasRole = (role) => {
    if (!user) return false;
    
    // Check both the primary role field and roles array
    const userRole = user.role || '';
    const userRoles = user.roles || [];
    
    // Normalize role names to lowercase for comparison
    const normalizedRole = role.toLowerCase();
    const normalizedUserRole = userRole.toLowerCase();
    const normalizedUserRoles = userRoles.map(r => {
      if (typeof r === 'string') return r.toLowerCase();
      return '';
    });
    
    // Special mapping for role names
    const roleMap = {
      'admin': ['admin', 'profile_5'],
      'finance': ['finance', 'profile_28'],
      'airports division manager': ['airports_manager', 'profile_25'],
      'business development': ['business_development', 'profile_27'],
      'pmo': ['pmo', 'profile_20'],
      'employee': ['employee', 'profile_3'],
      'supplier': ['supplier', 'profile_29', 'profile_34']
    };
    
    // Check if the role matches
    const rolesToCheck = roleMap[normalizedRole] || [normalizedRole];
    
    return rolesToCheck.some(r => 
      normalizedUserRole === r || normalizedUserRoles.includes(r)
    );
  };

  const isAdmin = () => {
    return hasRole('Admin') || hasRole('PMO');
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      logout,
      hasRole,
      isAdmin
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
